/*
 * File: GetStudentLedger.ts
 * Author: Austin Robinson
 * Date Created: 7/28/2023
 * Copyright (c) DrivingDoc 2023
 */

import { privateApi } from "api";
import { createQuery } from "react-query-kit";

export type GetStudentLedgerParams = {
  id: string;
  showVoided: boolean;
};

export type LedgerEntry = {
  id: string;
  student_user_id: string;
  date: string;
  type: "charge" | "payment" | "refund";
  memo: string | null;
  method: string | null;
  amount: string | null;
  debit: string | null;
  credit: string | null;
  balance: string | null;
  voided: boolean;
  reason_voided: string | null;
  voided_by: string | null;
  voided_at: Date | null;
  user: {
    tenant_id: string;
    id: string;
    title: string | null;
    first_name: string | null;
    middle_name: string | null;
    last_name: string | null;
    nickname: string | null;
    email: string | null;
    date_of_birth: string | null;
    sex: string | null;
    active: boolean;
    created_at: string | null;
    updated_at: string | null;
    created_by: string | null;
    updated_by: string | null;
    des_id: string | null;
  };
  payment_method: {
    tenant_id: string;
    id: string;
    name: string;
    enabled: boolean;
    allow_method_for_payment: boolean;
    allow_method_for_refund: boolean;
  } | null;
};

async function getStudentLedger(params: GetStudentLedgerParams) {
  if (!params.id) {
    return null;
  }
  try {
    // make api call
    const result = await privateApi.post(
      `${import.meta.env.VITE_API_BASE_URL}/users/students/${params.id}/ledger`,
      {
        showVoided: params?.showVoided,
      }
    );

    // return the data object
    return result.data as LedgerEntry[];
  } catch (e) {
    return null;
  }
}

const useGetStudentLedger = createQuery<
  LedgerEntry[] | null,
  GetStudentLedgerParams,
  Error
>({
  queryKey: ["student_ledger"],
  fetcher: (variables) => getStudentLedger(variables),
});

export { getStudentLedger, useGetStudentLedger };
